import { useParams, Link } from 'react-router-dom';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronLeft, BarChart3, ArrowRight } from 'lucide-react';
import { EventsPageShell } from './components/EventsPageShell';
import { getEventManagementBadge, getEventManagementOptions } from '@/lib/event-management-nav';

export default function EventManagementPage() {
  const { communityId, eventId } = useParams();

  const { data: registrationData } = useSWR(
    eventId ? `/api/events/${eventId}/registrations` : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  const { data: eventData } = useSWR(eventId ? `/api/events/${eventId}` : null, (url: string) =>
    axiosInstance.get(url).then((res) => res.data)
  );

  const event = eventData?.event;
  const registrationCount = registrationData?.registrations?.length || 0;
  const resolvedEventId = event?.id || eventId || '';
  const navCtx = { communityId: communityId || '', eventId: resolvedEventId };
  const managementOptions = getEventManagementOptions();

  return (
    <EventsPageShell>
      <div className="space-y-8">
        <div className="space-y-2">
          <Link
            to={`/${communityId}/events/${resolvedEventId}`}
            className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center w-fit mb-4 group"
          >
            <div className="bg-muted p-1 rounded-md mr-2 group-hover:bg-primary/10 transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </div>
            Back to Event Details
          </Link>
          <div className="flex flex-col gap-2">
            <h1 className="text-4xl font-black tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/70">
              Management Hub
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl leading-relaxed">
              Your command center for orchestrating a flawless event experience.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
          {managementOptions.map((option) => {
            const badge = getEventManagementBadge(option.key, registrationCount);
            return (
              <Link key={option.key} to={option.getHref(navCtx)} className="block group">
                <Card className="h-full relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 border-border/40 bg-card/50 backdrop-blur-sm group-hover:border-primary/30">
                  <div
                    className={`absolute top-0 left-0 w-1 h-full ${option.color.replace('text', 'bg')} opacity-40`}
                  />
                  <CardHeader className="flex flex-row items-center gap-4 pb-3">
                    <div
                      className={`p-4 rounded-2xl ${option.bgColor} shadow-sm group-hover:scale-110 transition-transform duration-300`}
                    >
                      <option.icon className={`w-7 h-7 ${option.color}`} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl font-bold tracking-tight group-hover:text-primary transition-colors">
                        {option.title}
                      </CardTitle>
                      {badge ? (
                        <div className="mt-1">
                          <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full uppercase tracking-widest font-bold border border-primary/20">
                            {badge}
                          </span>
                        </div>
                      ) : null}
                    </div>
                    <div className="bg-muted/50 p-2 rounded-full opacity-0 group-hover:opacity-100 group-hover:bg-primary/10 transition-all duration-300">
                      <ArrowRight className="w-5 h-5 text-primary" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-muted-foreground leading-relaxed line-clamp-2">
                      {option.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        <Card className="bg-muted/30 border-dashed border-2 overflow-hidden relative group/stats">
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 blur-3xl transition-all duration-500 group-hover/stats:bg-primary/10" />
          <CardHeader>
            <CardTitle className="text-xl font-bold flex items-center gap-3">
              <div className="bg-primary/10 p-2 rounded-lg">
                <BarChart3 className="w-5 h-5 text-primary" />
              </div>
              Quick Stats
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="space-y-1 relative">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
                Total Attendees
              </p>
              <div className="flex items-baseline gap-1">
                <p className="text-4xl font-black tracking-tighter">{registrationCount}</p>
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </EventsPageShell>
  );
}
